import { useEffect, useMemo, useState } from "react";
import { CalendarClock, Crown } from "lucide-react";
import { fmtDate, fmtDuration } from "../lib/labels";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";

const useNow = () => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
};

export const SeasonClock = ({ season }) => {
  const now = useNow();
  const offset = useMemo(() => (season ? Date.parse(season.server_time) - Date.now() : 0), [season]);
  if (!season) return null;
  const left = Math.max(0, Math.floor((Date.parse(season.next_reset_at) - now - offset) / 1000));
  return (
    <div className="panel flex items-center gap-5 px-5 py-4" data-testid="season-clock">
      <div>
        <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500">Temporada</div>
        <div className="font-display text-3xl font-bold text-emerald-400" data-testid="season-number">#{season.number}</div>
      </div>
      <div className="h-10 w-px bg-slate-800" />
      <div>
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500"><CalendarClock className="h-3 w-3" />Reinicia em</div>
        <div className="font-mono text-2xl font-bold tabular-nums text-slate-100" data-testid="season-countdown">{fmtDuration(left)}</div>
        <div className="font-mono text-[10px] text-slate-500">{fmtDate(season.next_reset_at)} (Lisboa)</div>
      </div>
    </div>
  );
};

export const ChampionBanner = ({ season }) => {
  if (season?.last_season_number == null) return null;
  const c = season.last_champion;
  return (
    <div className="panel fade-up relative mb-8 overflow-hidden border-amber-500/30 px-6 py-5" data-testid="champion-banner"
      style={{ boxShadow: c ? `inset 4px 0 0 ${c.color}` : undefined }}>
      <div className="flex flex-wrap items-center gap-5">
        <Crown className="h-8 w-8 text-amber-400" />
        <div className="flex-1">
          <div className="eyebrow text-amber-400">Campeã da temporada #{season.last_season_number}</div>
          {c ? (
            <div className="mt-1 font-display text-2xl font-bold uppercase tracking-wider" style={{ color: c.color }} data-testid="champion-name">{c.label}</div>
          ) : (
            <div className="mt-1 text-lg text-slate-400" data-testid="champion-name">Sem campeã (nenhuma organização pontuou)</div>
          )}
        </div>
        {c && (
          <div className="flex gap-6 font-mono text-sm">
            <span><b className="text-emerald-400">{c.points}</b> pts</span><span><b>{c.wins}</b> V</span><span><b>{c.kills}</b> kills</span>
          </div>
        )}
      </div>
    </div>
  );
};

const Standings = ({ rows, id }) => (
  <table className="w-full text-left text-sm" data-testid={`season-standings-${id}`}>
    <thead className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
      <tr><th className="py-2">#</th><th>Organização</th><th className="text-right">Pontos</th><th className="text-right">V</th><th className="text-right">D</th><th className="text-right">Kills</th><th className="text-right">Zonas</th></tr>
    </thead>
    <tbody>
      {rows.map((o) => (
        <tr key={o.id} className="border-t border-slate-800/70">
          <td className="py-2 font-mono text-slate-400">{o.position}</td>
          <td><span className="mr-2 inline-block h-2.5 w-2.5 rounded-full" style={{ background: o.color }} />{o.label}</td>
          <td className="text-right font-mono font-bold text-emerald-400">{o.points}</td>
          <td className="text-right font-mono">{o.wins}</td><td className="text-right font-mono">{o.losses}</td>
          <td className="text-right font-mono">{o.kills}</td><td className="text-right font-mono">{o.zones_held}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export const SeasonHistory = ({ seasons }) => {
  if (seasons && seasons.length === 0) {
    return <div className="panel p-8 text-center font-mono text-sm text-slate-500" data-testid="season-history-empty">Ainda não há temporadas terminadas.</div>;
  }
  return (
    <Accordion type="multiple" className="space-y-3" data-testid="season-history">
      {(seasons || []).map((s) => (
        <AccordionItem key={s.id} value={s.id} className="panel border-b-0 px-5" data-testid={`season-item-${s.number}`}>
          <AccordionTrigger className="hover:no-underline" data-testid={`season-toggle-${s.number}`}>
            <div className="flex flex-1 flex-wrap items-center gap-x-6 gap-y-2 text-left">
              <span className="font-display text-lg font-bold">Temporada #{s.number}</span>
              <span className="font-mono text-xs text-slate-500">{fmtDate(s.started_at)} → {fmtDate(s.ended_at)}</span>
              <span className="flex items-center gap-2 text-sm">
                <Crown className="h-4 w-4 text-amber-400" />
                {s.champion ? <b style={{ color: s.champion.color }}>{s.champion.label} · {s.champion.points} pts</b> : <span className="text-slate-500">Sem campeã</span>}
              </span>
              <span className="flex gap-2">
                {s.standings.slice(0, 3).map((o) => (
                  <span key={o.id} className="border border-slate-800 px-2 py-0.5 font-mono text-[11px] text-slate-400">#{o.position} {o.label} {o.points}</span>
                ))}
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent><Standings rows={s.standings} id={s.number} /></AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};
